import type { StoryContentType } from '~/types/story'
import type { UseSeoMetaInput } from '@unhead/vue'

export type UsePageMetaProps = {
  blok: Ref<StoryContentType | undefined>
  seoImageUrl?: Ref<string | undefined>
}

export default function usePageMeta({
  blok,
  seoImageUrl = ref(),
}: UsePageMetaProps) {
  const {
    public: { algoliaVerification },
  } = useRuntimeConfig()
  const route = useRoute()
  const site = useSiteConfig()
  const storyContent = useStoryContent({ blok })
  const { firstPublishedAt, displayDate } = usePage()

  const seoData = computed(() => blok.value?.seo)

  const title = computed(
    () =>
      storyContent?.pageBanner.value?.title?.value ||
      storyContent?.title.value ||
      ogTitle.value,
  )

  const ogTitle = computed(
    (): string =>
      seoData.value?.og_title ||
      seoData.value?.title ||
      storyContent?.pageBanner.value?.title?.value ||
      storyContent?.title.value ||
      site?.name ||
      '',
  )

  const description = computed(
    (): string =>
      seoData.value?.og_description ||
      storyContent?.introduction.value ||
      site?.description ||
      '',
  )

  const image = computed(
    (): string =>
      seoData.value?.og_image ||
      seoImageUrl?.value ||
      storyContent?.seoGraphicMedia.value ||
      storyContent?.media?.value?.filename ||
      site?.image ||
      '',
  )

  const safeMeta = computed(() => {
    try {
      return JSON.stringify(storyContent?.meta.value)
    } catch (err) {
      console.warn('Error parsing safe meta', err)
      return '{}'
    }
  })

  // split search_helper into an array on newlines, remove duplicates and empty strings, then join with commas
  const searchHelper = computed(
    (): string =>
      blok.value?.search_helper
        ?.split('\n')
        .filter(
          (item: string, index: number, self: string[]) =>
            item !== '' && self.indexOf(item) === index,
        )
        .join(',') || '',
  )

  const schema = computed(() => ({
    title,
    description,
    audiences: blok.value?.audiences?.join(',') || [],
    bannerImage: blok.value?.intro?.[0]?.image?.filename || '',
    component: blok.value?.component || '',
    displayDate: displayDate.value || '',
    firstPublishedAt: firstPublishedAt.value || '',
    fullSlug: route.fullPath,
    icon: blok.value?.icon || '',
    image: blok.value?.image?.filename || image.value || '',
    logo: blok.value?.logo?.filename || '',
    meta: safeMeta.value,
    organisation: blok.value?.organisation || '',
    organisationLink: blok.value?.link?.url || '',
    searchHelper: searchHelper.value,
    serviceType: blok.value?.service_type?.join(',') || '',
    states: blok.value?.states?.join(',') || [],
    topics: blok.value?.topics?.join(',') || [],
    type: blok.value?.type || '',
  }))

  const searchIndexMetaSchema = computed(() =>
    Object.entries(schema.value).map(([key, value]) => ({
      name: `compass:${kebabCase(key)}`,
      content: unref(value),
    })),
  )

  // Set search index meta schema in page head - this is used by Algolia to index the page properties
  useHead({
    meta: [
      ...searchIndexMetaSchema.value,
      {
        name: 'algolia-site-verification',
        content: algoliaVerification?.code || '',
      },
    ],
  })

  // Set page meta - adds standard meta tags to the page head
  const pageMeta: UseSeoMetaInput = {
    title,
    ogTitle,
    description,
    ogDescription: description,
    ogImage: image,
    twitterCard: 'summary_large_image',
    twitterImage: image,
  }

  useSeoMeta(pageMeta)
}
